import React from 'react';
import PropTypes from 'prop-types';
import { min, max, isEmpty } from 'lodash';
import { Form } from '@sparefoot/react-core';
import {
	compose, defaultProps, withHandlers, withStateHandlers, withPropsOnChange
} from 'recompose';

import {
	storageTypeOptions,
	amenityOptions,
	storageSizeOptions,
	vehicleSizeOptions
} from 'config/filterSearchHeroOptions';

import { MultiSelect } from 'components/core/MultiSelect';
import { Button } from 'components/core/Button';
import { Garage, Size } from 'components/svg';
import { GoogleAutocomplete } from 'components/core/GoogleAutocomplete';

import './FilterSearchBox.scss';

export const enhance = compose(
	defaultProps({
		storageOptions: { ...storageTypeOptions, ...amenityOptions },
		storageSizes: storageSizeOptions,
		vehicleSizes: vehicleSizeOptions,
		defaultStorageSizeOption: 'all-sizes',
		defaultStorageTypeOption: 'storage'
	}),
	withStateHandlers(
		// handlers for the state of the dropdowns
		({ defaultStorageSizeOption,
			defaultStorageTypeOption }) => ({
			selectedSizeOptions: [defaultStorageSizeOption],
			selectedTypeOptions: [defaultStorageTypeOption],
			locationParams: {},
			instance: null
		}),
		{
			setLocationParams: () => (googleLocation) => ({
				locationParams: { ...googleLocation }
			}),
			getRef: () => (instance) => ({ instance }),
			setSelectedTypeOptions: (state, { defaultStorageSizeOption }) => (options, target) => {
				// when the user selects a type, if it's one of the main storage types
				// reset the selected size to the default state
				if (Object.keys(storageTypeOptions).includes(target.value)) {
					return {
						selectedTypeOptions: options,
						selectedSizeOptions: [defaultStorageSizeOption]
					};
				}
				return { selectedTypeOptions: options };
			},
			setSelectedSizeOptions: (state, { defaultStorageSizeOption }) => (options, target) => {
				// if the user selects the default, clear the other options
				if (target.value === defaultStorageSizeOption) {
					return {
						selectedSizeOptions: [target.value]
					};
				}
				// if the user deselects all the options, default to all sizes
				if (isEmpty(options)) {
					return {
						selectedSizeOptions: [defaultStorageSizeOption]
					};
				}
				// if the user selects something other than the default
				// don't include the default in the new selected list
				return {
					selectedSizeOptions: options
					.filter((o) => o !== defaultStorageSizeOption)
				};
			}
		}
	),
	withPropsOnChange(
		// compute new values depending on what the selected type is
		['selectedTypeOptions'],
		({ selectedTypeOptions }) => ({
			// bool for if we should show the vehicle storage sizes
			showVehicleSizes: selectedTypeOptions.includes('vehicle')
		})

	),
	withHandlers({
		handleSubmit: ({
			instance,
			selectedSizeOptions,
			selectedTypeOptions,
			searchLocation,
			locationParams,
			handleSearch
		}) => (event) => {
			// this is where we catch the submit event, collect the current
			// state values, and set up the object we'll pass to the handleSearch
			// from `withSearchHandlers`.

			// Storage search example
			// https://www.selfstorage.com/search?amenities[]=alarm&distance=30&location=78666&page=1&sort=yielddistancelowltveconmir100forced&type=storage

			// Vehicle search example
			// https://www.selfstorage.com/search?amenities[]=alarm&distance=30&length=25-29.99&location=78666&page=1&sort=yielddistancelowltveconmir100forced&type=vehicle

			event.preventDefault(); // Don't actually search yet

			let searchParams = {
				...locationParams
			};

			// two of the types in the dropdown are for swapping between storage
			// and vehicle searches -- the `storageTypeOptions`.
			// anything that's selected that's not from that group is an amenity
			// so set this up to be an amenity search
			if (!selectedTypeOptions.some((o) => Object.keys(storageTypeOptions).includes(o))) {
				searchParams = {
					...searchParams,
					// this will be a single amenity, but the API will expect an array
					amenities: selectedTypeOptions,
					// by selecting an amenity, this is necessarily a storage search
					type: 'storage'
				};
			} else {
				searchParams = {
					...searchParams,
					// pick whatever the selected type option is, which should either be
					// `vehicle` or `storage`
					type: selectedTypeOptions[0]
				};
			}

			// the API can take a range, so find the biggest and smallest sizes and pass that as a range.
			if (!selectedSizeOptions.includes('all-sizes')) {
				const sizes = selectedSizeOptions
				.join('-')
				.split('-')
				.map((n) => Number(n));

				// vehicle search uses length param instead of sqft
				if (selectedTypeOptions.includes('vehicle')) {
					searchParams = {
						...searchParams,
						length: `${(min(sizes))}-${max(sizes)}`
					};
				} else {
					searchParams = {
						...searchParams,
						sqft: `${(min(sizes))}-${max(sizes)}`
					};
				}
			}

			// Finally, if didn't get a bunch of location info from google, just
			// use the value of the text box.
			if (!locationParams.locationSource && instance && (instance.value !== 'undefined')) {
				searchParams.location = instance.value;
			}

			if (!searchParams.location) {
				// if by this point there's no location key, we can assume the user
				// chose not to enter a location. Assume here that we should use the
				// CLP's location so we don't 404 them.
				searchParams.location =
					`${searchLocation.city}, ${searchLocation.state}`;
			}

			// TODO: Refactor withSearchHandler
			// withSearchHandler only expects the collection of location data
			// because it's usually only doing a location search. But for this
			// component, we have all the location data _and_ all the search info.

			// Because `withSearchHandler` ultimately spreads and includes everything
			// in the `locationParams` object and includes those when it builds
			// the URL, it will work -- but it's confusing that we're calling
			// things like the search type and amenities 'locationParams'.


			// TODO: should we include logic from `withSearchPageHandlers`?
			return handleSearch({ locationParams: searchParams });
		}
	})
);

export function FilterSearchBox({
	buttonText,
	id,
	inputLabel,
	getRef,
	useBrowserLocation,
	// dropdown data
	storageOptions,
	storageSizes,
	vehicleSizes,
	selectedSizeOptions,
	selectedTypeOptions,
	showVehicleSizes,
	// state handlers
	setSelectedTypeOptions,
	setSelectedSizeOptions,
	setLocationParams,
	handleSubmit
}) {
	return (
		<Form
			action="/search"
			className="filter-search-box"
			successLabel="success"
			failureLabel="fail"
			onSubmit={handleSubmit}
		>

			<GoogleAutocomplete
				className="location-search"
				segmentLabel="filter-searchbox location input field"
				placeholder={inputLabel}
				onChange={setLocationParams}
				getRef={getRef}
				id={id}
				name="location"
				label="Location"
				useBrowserLocation={useBrowserLocation}
			/>
			<MultiSelect
				label="Storage Options"
				icon={<Garage />}
				name="type"
				multiple={false}
				className="storage-filter"
				options={storageOptions}
				selected={selectedTypeOptions}
				onChange={setSelectedTypeOptions}
				segmentLabel="storage options"
			/>
			<MultiSelect
				label="Storage Sizes"
				icon={<Size />}
				name="size"
				className="size-filter"
				multiple
				options={showVehicleSizes ? vehicleSizes : storageSizes}
				selected={selectedSizeOptions}
				onChange={setSelectedSizeOptions}
				segmentLabel="storage sizes"
			/>


			<Button
				primary
				type="submit"
				segmentLabel="facility search button"
				className="submit-button"
			>
				{buttonText}
			</Button>
		</Form>
	);
}

FilterSearchBox.propTypes = {
	getRef: PropTypes.func,
	setLocationParams: PropTypes.func.isRequired,
	handleSubmit: PropTypes.func.isRequired,
	id: PropTypes.string,
	inputLabel: PropTypes.string,
	buttonText: PropTypes.string,
	useBrowserLocation: PropTypes.bool,

	setSelectedTypeOptions: PropTypes.func.isRequired,
	setSelectedSizeOptions: PropTypes.func.isRequired,
	selectedSizeOptions: PropTypes.arrayOf(
		PropTypes.string
	),
	selectedTypeOptions: PropTypes.arrayOf(
		PropTypes.string
	),
	showVehicleSizes: PropTypes.bool,
	storageSizes: PropTypes.objectOf(PropTypes.shape({
		label: PropTypes.string,
		name: PropTypes.string,
		value: PropTypes.string
	})),
	vehicleSizes: PropTypes.objectOf(PropTypes.shape({
		label: PropTypes.string,
		name: PropTypes.string,
		value: PropTypes.string
	})),
	storageOptions: PropTypes.objectOf(
		PropTypes.shape({
			label: PropTypes.string,
			name: PropTypes.string,
			value: PropTypes.string
		})
	)
};

FilterSearchBox.defaultProps = {
	inputLabel: 'Enter ZIP Code',
	buttonText: 'Find storage'
};

export default enhance(FilterSearchBox);
